import { Dialog } from '@aecom-ai-engineering/react-components';
import { TriangleAlert } from 'lucide-react';
import { FC, ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /** The question, as text or rich content. */
  children: ReactNode;
  confirmLabel: string;
  /**
   * Called once the dialog is gone and focus has been handed back, so that whatever the
   * confirmation starts can take focus without the dialog taking it straight back.
   */
  onClosed?: () => void;
}

/** A warning the user has to confirm before something that cannot be undone goes ahead. */
export const ConfirmDialog: FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  children,
  confirmLabel,
  onClosed,
}) => {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(open);
  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;

  useEffect(() => {
    const justClosed = wasOpenRef.current && !open;
    wasOpenRef.current = open;
    if (!justClosed) return;

    // The dialog hands focus back to what opened it only as it leaves, so wait for that.
    let frame = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    const check = () => {
      const content = contentRef.current;
      if (!content || !content.isConnected || content.hidden || attempts++ > 60) {
        timer = setTimeout(() => onClosedRef.current?.(), 0);
        return;
      }
      frame = requestAnimationFrame(check);
    };
    frame = requestAnimationFrame(check);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [open]);

  return (
    <Dialog
      ref={contentRef}
      // Sized by its message rather than by the design system's fixed dialog height.
      type="desktop-scrollable"
      className="h-auto"
      open={open}
      onOpenChange={({ open: isOpen }) => {
        if (!isOpen) onClose();
      }}
      title={
        <span className="flex items-center gap-2">
          <TriangleAlert size={20} aria-hidden="true" className="shrink-0" />
          {title}
        </span>
      }
      primaryAction={{ label: confirmLabel, onClick: onConfirm }}
      secondaryAction={{ label: t('meeting-secretary.meetings.confirm-cancel'), onClick: onClose }}
    >
      {children}
    </Dialog>
  );
};
